import React, {useEffect, useLayoutEffect, useState} from 'react';
import {RootState} from "../../../../../reducers";
import {
    getAvailabilities,
    getCategories,
    getItemGroups,
    getItems,
    getMenus,
    getModifierGroups,
    getPromotions
} from "../../../../services/Menu/reducer";
import {getSession, selectLocale} from "../../../../services/Session/reducer";
import {getStoreConfig} from "../../../../services/Store/reducer";
import {connect} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import AvailableHelper from "../../../../services/Menu/services/Availability/AvailableHelper";
import HoursHelper from "../../../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../../../services/Common/Date/DateService";
import {AvailabilityInfo, OrderType} from "../../../../services/Api/types";
import {useLocation} from "react-router-dom";
import {anyMenuHasPickDate} from "../../../../components/Cart/utils";
import CategoryHelper from "../../../../services/Menu/services/Category/CategoryHelper";
import {getParsedSessionOrderType} from "../../../../utils/order";
import PromotionHelper from "../../../../services/Menu/services/Promotion/PromotionHelper";
import {getCart} from "../../../../services/Cart/reducer";
import ItemAvailabilityHelper from "../../../../services/Menu/services/Item/ItemAvailabilityHelper";
import {ReactComponent as ArrowRightIco} from "../../../../../images/icons/arrow-right.svg";
import CartChangeOrderDetailsModal
    from "../../../../components/Cart/components/CartChangeOrderDetailsModal/CartChangeOrderDetailsModal";
import StateItemHelper from "../../../../services/Menu/services/State/StateItemHelper";
import ItemViewHelper from "../../../../services/Menu/services/Item/ItemViewHelper";
import StoreUtils from "../../../../services/Store/StoreUtils";
import AgeRestrictedContext from "../../../../services/Common/AgeRestrictedContext";

interface OwnProps {
    product: Record<string, any>;
    setIsProductAvailable: React.Dispatch<React.SetStateAction<boolean>>;
    productView: Record<string, any>;
    productItemGroup: Record<string, any> | undefined;
    productState: Record<string, any>;
    updateProductState: (productState: Record<string, any>) => void;
    setIsAvailabilityInfo: React.Dispatch<React.SetStateAction<boolean>>;
    setHasNoAvailableInfo: React.Dispatch<React.SetStateAction<boolean>>;
    menuId: number | undefined;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const findProductInCategoryItems = (items: Record<string, any>[], product: Record<string, any>) => {
    return items.find((item: Record<string, any>) => {
        if (item.type === "ITEM_GROUP") {
            return item.items?.some((itemItem: Record<string, any>) => itemItem.item_id === product.id) || item.item.id === product.id;
        }

        return item.id === product.id;
    });
}

const ProductAvailability = ({
                                 availabilities,
                                 items,
                                 product,
                                 session,
                                 locale,
                                 config,
                                 menus,
                                 categories,
                                 promotions,
                                 cart,
                                 itemGroups,
                                 setIsProductAvailable,
                                 productView,
                                 productItemGroup,
                                 updateProductState,
                                 productState,
                                 modifierGroups,
                                 setIsAvailabilityInfo,
                                 setHasNoAvailableInfo,
                                 menuId
                             }: Props) => {
    const availability = AvailableHelper.getAvailability(availabilities, product.availability_id);
    const isOpen = availability ? HoursHelper.isOpen(availability.hours, DateService.emptyOrNow(session.pickup_at)) && AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)) : true;
    const location = useLocation();
    const categoryIdsFromUrlState = location.state?.categoryIds;
    const categoryIds = categoryIdsFromUrlState ?? [ItemAvailabilityHelper.getCategoryWithNearestAvailableDateForItem(product, categories, menus, session, menuId, productItemGroup)?.id].filter(Boolean);
    const categoriesByIds = categoryIds && categoryIds.length > 0 ? categories.filter((c: Record<string, any>) => c.status === 'ENABLED' && categoryIds.includes(c.id)) : undefined;
    const {t} = useTranslation();
    let categoryMenus: Record<string, any>[] = [];
    const isReadonly = StoreUtils.isOrderingReadonly(config);
    const parsedCurrentType = getParsedSessionOrderType(session.order_type as OrderType);
    const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
    let parsedInfos: string[] = [];

    const getParsedCategory = () => {
        let parsedCategory: Record<string, any> | undefined = undefined;

        if (!categoriesByIds || categoriesByIds.length === 0) return parsedCategory;

        const availablePromotions = PromotionHelper.getActivePromotionsForCategory(promotions, cart.total);
        parsedCategory = CategoryHelper.getParsedCategories(
            categoriesByIds,
            items,
            availabilities,
            availablePromotions,
            session,
            cart,
            itemGroups,
            menus,
            AgeRestrictedContext.isAccepted(StoreUtils.isAgeRestrictionDisabled(config)),
            undefined,
            undefined,
            isReadonly)[0];

        if (!parsedCategory || parsedCategory.entities.length === 0) return undefined;

        const menusWithCategory = CategoryHelper.getMenusForCategoryByIds(parsedCategory, menus);
        const itemFromCategoryItems = findProductInCategoryItems(parsedCategory.entities, product);
        const menusWithCategoryForItem = itemFromCategoryItems ? menusWithCategory.filter((menu: Record<string, any>) => itemFromCategoryItems.menuAvailabilitiesIds.includes(menu.availability_id)) : [...menusWithCategory];
        const menuAvailabilities = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForItem.map((menu: any) => menu.availability_id));
        categoryMenus = [...menusWithCategoryForItem];

        return menuAvailabilities.length > 0 ? parsedCategory : undefined;
    }

    const finalCategory = getParsedCategory();

    const menusWithCategoryForCurrentOrderType = categoryMenus.filter((menu: any) => menu[parsedCurrentType.toLowerCase()] === true);
    const menuAvailabilitiesForCurrentType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForCurrentOrderType.map((menu: any) => menu.availability_id));
    const isCategoryAvailableByHoursForCurrentType = HoursHelper.isOpen(menuAvailabilitiesForCurrentType.flatMap((availability: any) => availability.hours), DateService.emptyOrNow(session.pickup_at));
    const isCategoryAvailableByAvailabilityDates = menuAvailabilitiesForCurrentType.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)));
    const isCategoryAvailable = isCategoryAvailableByHoursForCurrentType && isCategoryAvailableByAvailabilityDates && menusWithCategoryForCurrentOrderType.length > 0;
    const availableCategoryItems = CategoryHelper.getAvailableCategoryItems(availabilities, session.pickup_at, finalCategory?.entities || []);
    const isProductInAvailableCategoryItems = !!findProductInCategoryItems(availableCategoryItems, product);
    const isCategoryFinallyAvailable = isReadonly ? true : isCategoryAvailable ? isCategoryAvailable && !!availableCategoryItems.length : isCategoryAvailable;
    const isAvailable = isCategoryFinallyAvailable && isOpen && !productView.disabled && isProductInAvailableCategoryItems;
    const itemFromCategoryItems = findProductInCategoryItems(finalCategory?.entities || [], product);
    let productAvailabilityInfos: AvailabilityInfo[] = []

    useEffect(() => {
        setIsProductAvailable(isAvailable);
    }, [isAvailable, setIsProductAvailable]);

    useLayoutEffect(() => {
        setIsAvailabilityInfo((parsedInfos.length > 0 && !isAvailable) || !finalCategory || !itemFromCategoryItems);
        setHasNoAvailableInfo(!finalCategory || !itemFromCategoryItems || (productAvailabilityInfos.length === 1 && productAvailabilityInfos[0].isEverAvailable === false))
    }, [parsedInfos, isAvailable, finalCategory, itemFromCategoryItems, productAvailabilityInfos]);

    if (!finalCategory || !itemFromCategoryItems) return (
        <div className="product-availability-info-container d-flex flex-column w-100">
            <p className="card-text text-warning fw-normal product-availability-info w-100">
                {t("common.word.not_available")}
            </p>
        </div>
    );

    if (isAvailable || !config) return <></>;

    productAvailabilityInfos = ItemAvailabilityHelper.getItemAvailabilityInfo(itemFromCategoryItems, availability, config, menus, availabilities, finalCategory, session.pickup_at, isReadonly ? undefined : session.order_type as OrderType, locale, t, !!isReadonly);
    const categoryAvailabilityInfos = CategoryHelper.getAvailabilityInfo(finalCategory, categoryMenus, [itemFromCategoryItems], availabilities, config, isReadonly ? undefined : session.order_type as OrderType, session.pickup_at, t, locale, true);

    const getFinalAvailabilityInfos = () => {
        if (itemFromCategoryItems.item_always_available) {
            return isReadonly ? [] : categoryAvailabilityInfos;
        }

        if (isReadonly) return productAvailabilityInfos;

        return (isCategoryFinallyAvailable && !isProductInAvailableCategoryItems) || (!isCategoryFinallyAvailable && !isProductInAvailableCategoryItems) ?
            productAvailabilityInfos : categoryAvailabilityInfos ?? productAvailabilityInfos;
    }

    const getAvailabilityDescription = () => {
        const canChangePickupAt = StoreUtils.isPickDateEnabled(config) && anyMenuHasPickDate(categoryMenus, parsedCurrentType)
        const infoForCurrentOrderType = getFinalAvailabilityInfos().find(info => info.includeCurrentType || info.currentType);
        const infoForOtherOrderTypes = getFinalAvailabilityInfos().filter(info => !info.includeCurrentType && !info.currentType);

        if (isReadonly) return "";

        if (
            (!infoForCurrentOrderType && infoForOtherOrderTypes.length === 1 && Object.keys(infoForOtherOrderTypes[0]).length === 1)
            || (!infoForOtherOrderTypes && infoForCurrentOrderType && Object.keys(infoForCurrentOrderType).length === 1)
        ) {
            return "";
        }
        if (infoForCurrentOrderType && infoForOtherOrderTypes.length > 0 && !infoForCurrentOrderType.isNowAvailable && infoForOtherOrderTypes.every(info => !info.isNowAvailable) && canChangePickupAt) {
            return t("modules.menu.field.change_pickup_date.title");
        }

        if (infoForCurrentOrderType && !infoForCurrentOrderType.isNowAvailable && infoForOtherOrderTypes.some(info => info.isNowAvailable)) {
            return t("modules.menu.field.change_pickup_date_or_order_type.title");
        }

        if (!infoForCurrentOrderType && infoForOtherOrderTypes.length > 0 && infoForOtherOrderTypes.every(info => !info.isNowAvailable)) {
            return t("modules.menu.field.change_pickup_date_or_order_type.title");
        }

        if (!infoForCurrentOrderType && infoForOtherOrderTypes.some(info => info.isNowAvailable)) {
            return t("modules.menu.field.change_order_type.title");
        }

        if (infoForCurrentOrderType && infoForOtherOrderTypes.length === 0 && !infoForCurrentOrderType.isNowAvailable && canChangePickupAt) {
            return t("modules.menu.field.change_pickup_date.title");
        }

        return "";
    }

    parsedInfos = ItemAvailabilityHelper.getParsedAvailabilityInfos(getFinalAvailabilityInfos());
    const availabilityDescription = getAvailabilityDescription();

    const onSave = (session: Record<string, any>) => {
        const parsedItemGroup = productItemGroup ? ItemViewHelper.parseItemGroup(
            productItemGroup, items, availabilities, [],
            session.pickup_at, 0, cart, session.order_type
        ) : undefined;
        const itemId = parsedItemGroup ? parsedItemGroup.items.find((item: Record<string, any>) => item.order_types.length === 0 || item.order_types.includes(session.order_type)).item_id : product.id;
        const newItemState = StateItemHelper.create(itemId, items, modifierGroups, session.order_type);
        StateItemHelper.refreshItemPrice(newItemState, PromotionHelper.getForItem(promotions, productState.item_id), cart);
        updateProductState(newItemState)
    }

    if (parsedInfos.length === 0) return <></>;

    return (
        <>
            <div
                className={`product-availability-info-container d-flex w-100 align-items-center ${availabilityDescription ? "cursor-pointer" : ""} ${StoreUtils.isOrderingReadonly(config) ? "mb-0" : ""}`}
                onClick={availabilityDescription ? () => setShowOrderDetailsModal(true) : undefined}
            >
                <div className="d-flex flex-column w-100">
                    <p className="text-warning fw-normal mb-0">
                        {parsedInfos.join(". ")}
                    </p>
                    {availabilityDescription && (
                        <p className="fw-normal mb-0 description">
                            {availabilityDescription}
                        </p>
                    )}
                </div>
                {availabilityDescription && <ArrowRightIco/>}
            </div>
            {
                showOrderDetailsModal && <CartChangeOrderDetailsModal show={showOrderDetailsModal} onSave={onSave}
                                                                      onHide={() => setShowOrderDetailsModal(false)}
                                                                      menuId={menuId}
                />
            }
        </>
    )
}
const mapStateToProps = (state: RootState) => ({
    availabilities: getAvailabilities(state.menu),
    session: getSession(state.session),
    locale: selectLocale(state.session),
    config: getStoreConfig(state.config),
    menus: getMenus(state.menu),
    items: getItems(state.menu),
    categories: getCategories(state.menu),
    promotions: getPromotions(state.menu),
    cart: getCart(state.cart),
    itemGroups: getItemGroups(state.menu),
    modifierGroups: getModifierGroups(state.menu)
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(ProductAvailability));
